import { useEffect, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { Search, Scale, MapPin, Star } from 'lucide-react';
import { useCart } from '../lib/cart';
import NotifyPriceDropButton from '../components/NotifyPriceDropButton';

export default function SearchResults() {
  const [sp, setSp] = useSearchParams();
  const q = sp.get('q') || '';
  const [input, setInput] = useState(q);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();
  const cart = useCart();

  useEffect(() => {
    setInput(q);
    if (!q) { setItems([]); return; }
    setLoading(true);
    api.get('/search/products', { params: { q } }).then(r => setItems(r.data)).finally(() => setLoading(false));
  }, [q]);

  const submit = (e) => { e.preventDefault(); setSp({ q: input }); };

  // Group results by normalized product name to surface compare-worthy items
  const groups = items.reduce((acc, row) => {
    const key = row.product.name.toLowerCase().trim();
    if (!acc[key]) acc[key] = [];
    acc[key].push(row);
    return acc;
  }, {});
  const groupKeys = Object.keys(groups).sort((a, b) => groups[b].length - groups[a].length);

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-6 py-10">
      <h1 className="font-heading font-black text-3xl md:text-4xl">Search products</h1>
      <p className="text-ink/70 mt-1">Find a product across every store in your area.</p>

      <form onSubmit={submit} className="mt-5 flex items-center gap-2 rounded-full border border-border bg-white pl-5 pr-2 py-2 shadow-sm max-w-2xl">
        <Search className="w-4 h-4 text-muted-foreground"/>
        <input data-testid="search-input" value={input} onChange={e=>setInput(e.target.value)} placeholder="Try ‘atta’, ‘tomato’, ‘saree’…" className="flex-1 outline-none text-sm bg-transparent py-1.5"/>
        <button data-testid="search-submit" className="btn-primary !py-2">Search</button>
      </form>

      {loading && <div className="mt-8 text-ink/60">Searching…</div>}

      {!loading && q && items.length === 0 && (
        <div data-testid="search-empty" className="mt-10 card-soft p-8 text-center text-ink/60">
          No products found for "<b>{q}</b>". Try a shorter word like "atta", "rice" or "saree".
        </div>
      )}

      <div className="mt-8 space-y-6">
        {groupKeys.map(k => {
          const rows = groups[k];
          const sample = rows[0].product;
          const stores = new Set(rows.map(r => r.store.id));
          const canCompare = stores.size > 1 || rows.length > 1;
          return (
            <section key={k} className="card-soft p-5">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3">
                  <div className="w-14 h-14 rounded-lg bg-muted overflow-hidden">{sample.image_url && <img src={sample.image_url} alt="" className="w-full h-full object-cover"/>}</div>
                  <div>
                    <div className="font-heading font-bold text-lg leading-tight">{sample.name}</div>
                    <div className="text-xs text-ink/60">Available in {rows.length} listing{rows.length > 1 ? 's' : ''} across {stores.size} store{stores.size > 1 ? 's' : ''}</div>
                  </div>
                </div>
                {canCompare && (
                  <button data-testid={`compare-${k}`} onClick={() => nav(`/compare?q=${encodeURIComponent(sample.name)}`)} className="btn-outline !py-2"><Scale className="w-4 h-4"/>Compare {rows.length} options</button>
                )}
                <NotifyPriceDropButton productQuery={sample.name} testId={`notify-${k}`}/>
              </div>

              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                {rows.map(r => (
                  <Link to={`/stores/${r.store.id}`} key={r.product.id} data-testid={`search-row-${r.product.id}`} className="rounded-lg border border-border bg-white hover:border-[#E67E22] transition p-3 flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-ink truncate">{r.store.name} {r.store.verified && <span className="text-[10px] text-[#1B5E20] font-bold ml-1">✓ Verified</span>}</div>
                      <div className="text-xs text-ink/60 inline-flex items-center gap-3 mt-0.5">
                        <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3"/>{r.store.village}</span>
                        <span className="inline-flex items-center gap-1"><Star className="w-3 h-3 fill-[#F1C40F] text-[#F1C40F]"/>{r.store.rating?.toFixed?.(1)}</span>
                        <span className="capitalize">{r.product.quality_grade}</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-bold text-ink">₹{r.product.price}</div>
                      <div className="text-[10px] text-ink/50 uppercase tracking-wider">per {r.product.unit}</div>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}
